import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, ChevronDown, ToggleLeft, ToggleRight, Loader2, Plus, CheckCircle, XCircle, KeyRound, Copy, Trash2, Eye } from 'lucide-react';
import api from '../services/api';
import { useProject } from '../context/ProjectContext';

import RoleGuard from '../components/RoleGuard';

const Configuration: React.FC = () => {
  const { currentProject, refreshProjects, setCurrentProject } = useProject();
  const navigate = useNavigate();

  // const user = JSON.parse(localStorage.getItem('user') || '{}'); // Removed unused var
  const [isProjectLoading, setIsProjectLoading] = useState(false);
  const [config, setConfig] = useState<any>({
    name: '',
    code: '',
    domain: '',
    jira_enabled: false,
    jira_url: '',
    jira_query: '',
    confluence_enabled: false,
    confluence_url: '',
    confluence_page_id: '',
    jira_user: '',
    jira_api_key: '',
    confluence_user: '',
    confluence_api_key: '',
    ai_temperature: 0.5,
    ai_provider: 'openai',
    ai_model: 'gpt-4o',
    ai_api_key: '',
    max_cases: 50,
    include_negative: true,
    include_security: false,
    include_edge_cases: true,
    include_gherkin: false,
    langfuse_enabled: true,
  });
  const [domains, setDomains] = useState<{ value: string; label: string }[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [testingJira, setTestingJira] = useState(false);
  const [testingConfluence, setTestingConfluence] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [creatingKey, setCreatingKey] = useState(false);
  const [newKeyLabel, setNewKeyLabel] = useState('');
  const [rawKeyModal, setRawKeyModal] = useState<string | null>(null);  // shown ONCE on creation
  const [revokingKeyId, setRevokingKeyId] = useState<string | null>(null);

  useEffect(() => {
    loadOptions();
    loadApiKeys();
    if (currentProject) {
      loadProjectConfig(currentProject.value);
    }
  }, [currentProject]);

  const loadApiKeys = useCallback(async () => {
    setLoadingKeys(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/keys', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setApiKeys(await res.json());
    } catch { /* ignore */ } finally { setLoadingKeys(false); }
  }, []);

  const handleCreateKey = async () => {
    if (!newKeyLabel.trim()) return;
    setCreatingKey(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/keys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ label: newKeyLabel }),
      });
      const data = await res.json();
      setRawKeyModal(data.raw_key);  // shown ONCE
      setNewKeyLabel('');
      await loadApiKeys();
    } catch { setToast({ type: 'error', message: 'Error al crear la clave' }); }
    finally { setCreatingKey(false); }
  };

  const handleRevokeKey = async (keyId: string) => {
    if (!window.confirm('¿Revocar esta API Key? La acción es inmediata e irreversible.')) return;
    setRevokingKeyId(keyId);
    try {
      const token = localStorage.getItem('token');
      await fetch(`/api/keys/${keyId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      await loadApiKeys();
      setToast({ type: 'success', message: 'API Key revocada.' });
      setTimeout(() => setToast(null), 3000);
    } catch { setToast({ type: 'error', message: 'Error al revocar.' }); }
    finally { setRevokingKeyId(null); }
  };

  const loadProjectConfig = async (id: string) => {
    try {
      setIsProjectLoading(true);
      const data = await api.getProjectConfig(id);
      // Ensure provider exists in data or default to openai
      if (!data.ai_provider) data.ai_provider = 'openai';
      setConfig(data);
    } catch (error) {
      console.error('Failed to load project config:', error);
    } finally {
      setIsProjectLoading(false);
    }
  };

  const loadOptions = async () => {
    try {
      const [domainsData] = await Promise.all([
        api.getDomains(),
        // Models loaded via effect
      ]);
      setDomains(domainsData);
    } catch (error) {
      console.error('Failed to load options:', error);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (config.id) {
        await api.updateProject(config.id, config);
      } else {
        await api.createProject(config);
      }
      await refreshProjects();
      setToast({ type: 'success', message: '¡Configuración guardada con éxito!' });
      setTimeout(() => setToast(null), 3000);
    } catch (error) {
      console.error('Failed to save:', error);
      setToast({ type: 'error', message: 'Error al guardar la configuración' });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setIsSaving(false);
    }
  };

  const testJiraConnection = async () => {
    if (!config.jira_url) return;
    setTestingJira(true);
    try {
      const result = await api.testJiraConnection(config.jira_url);
      alert(result.message);
    } catch (error) {
      alert('Connection failed');
    } finally {
      setTestingJira(false);
    }
  };

  const testConfluenceConnection = async () => {
    const urlToTest = config.confluence_url || config.confluence_page_id;
    if (!urlToTest) return;
    setTestingConfluence(true);
    try {
      const result = await api.testConfluenceConnection(urlToTest);
      alert(result.message);
    } catch (error) {
      alert('Connection failed');
    } finally {
      setTestingConfluence(false);
    }
  };

  const handleDelete = async () => {
    if (!config.id) return;

    // Safety check with browser confirm (simple but effective for Admin)
    const isConfirmed = window.confirm(
      `⛔️ PELIGRO: ESTA ACCIÓN ES IRREVERSIBLE.\n\n` +
      `¿Estás seguro de que deseas eliminar eliminar el proyecto "${config.name}"?\n` +
      `Esto borrará todas las configuraciones asociadas.`
    );

    if (!isConfirmed) return;

    setIsSaving(true);
    try {
      const deletedProjectId = config.id;
      await api.deleteProject(deletedProjectId);
      setToast({ type: 'success', message: 'Proyecto eliminado correctamente.' });

      // If deleted project was the active one, clear it and redirect
      if (currentProject?.value === deletedProjectId) {
        setCurrentProject(null);
        localStorage.removeItem('currentProjectId');
      }

      // Refresh list and clear current selection
      await refreshProjects();

      // Redirect to project list
      navigate('/projects');


      // Redirect or clear form
      setConfig({
        name: '',
        code: '',
        domain: '',
        jira_enabled: false,
        jira_url: '',
        jira_query: '',
        confluence_enabled: false,
        confluence_url: '',
        confluence_page_id: '',
        jira_user: '',
        jira_api_key: '',
        confluence_user: '',
        confluence_api_key: '',
        ai_temperature: 0.5,
        ai_provider: 'openai',
        ai_model: 'gpt-4o',
        ai_api_key: '',
        max_cases: 50,
        include_negative: true,
        include_security: false,
        include_edge_cases: true,
        include_gherkin: false,
        langfuse_enabled: true,
      });

    } catch (error) {
      console.error('Failed to delete project:', error);
      setToast({ type: 'error', message: 'Error al eliminar el proyecto.' });
    } finally {
      setIsSaving(false);
    }
  };

  const Toggle = ({ enabled, onChange }: { enabled: boolean; onChange: () => void }) => (
    <button onClick={onChange} className="relative">
      {enabled ? (
        <ToggleRight className="w-10 h-6 text-primary" />
      ) : (
        <ToggleLeft className="w-10 h-6 text-slate-600" />
      )}
    </button>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {isProjectLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-800 p-8 rounded-2xl shadow-2xl flex flex-col items-center">
            <Loader2 className="w-10 h-10 text-primary animate-spin mb-4" />
            <p className="text-white font-medium">Cargando configuración...</p>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-2 fade-in duration-300">
          <div className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-xl border ${toast.type === 'success'
            ? 'bg-green-500/10 border-green-500/30 text-green-400'
            : 'bg-red-500/10 border-red-500/30 text-red-400'
            }`}>
            {toast.type === 'success' ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <XCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <p className="font-medium">{toast.message}</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-bold text-white">Configuración de Proyecto</h2>
            {config.id ? (
              <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-xs font-medium rounded-full border border-blue-500/30">
                {isProjectLoading ? 'Cargando...' : 'Modo Edición'}
              </span>
            ) : (
              <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-medium rounded-full border border-green-500/30">
                Nuevo Proyecto
              </span>
            )}
          </div>
          <p className="text-slate-400 mt-1">
            {config.id
              ? `Editando configuración para: ${config.name}`
              : 'Define un nuevo proyecto desde cero.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {config.id && (
            <RoleGuard allowed={['admin', 'qa_lead']}>
              <button
                onClick={() => {
                  setConfig({
                    name: '',
                    code: '',
                    domain: '',
                    jira_enabled: false,
                    jira_url: '',
                    jira_query: '',
                    confluence_enabled: false,
                    confluence_url: '',
                    confluence_page_id: '',
                    jira_user: '',
                    jira_api_key: '',
                    confluence_user: '',
                    confluence_api_key: '',
                    ai_temperature: 0.5,
                    ai_provider: 'openai',
                    ai_model: 'gpt-4o',
                    ai_api_key: '',
                    max_cases: 50,
                    include_negative: true,
                    include_security: false,
                    include_edge_cases: true,
                    include_gherkin: false,
                    langfuse_enabled: true,
                  });
                }}
                className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors border border-slate-700"
              >
                <Plus className="w-4 h-4" />
                <span>Crear Nuevo</span>
              </button>
            </RoleGuard>
          )}
          <span className="text-xs text-slate-500 hidden md:inline">Dashboard de Configuración</span>
        </div>
      </div>

      {/* Section 1: General Details */}
      <section className="bg-surface-dark border border-slate-800 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-6">
          <span className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-xs font-bold text-white">1</span>
          <h3 className="text-lg font-semibold text-white">Detalles Generales</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Nombre del Proyecto</label>
            <input
              type="text"
              value={config.name}
              onChange={(e) => setConfig({ ...config, name: e.target.value })}
              placeholder="Ej: Migración Core Bancario V2"
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Código de Proyecto (Opcional)</label>
            <input
              type="text"
              value={config.code}
              onChange={(e) => setConfig({ ...config, code: e.target.value })}
              placeholder="Ej: PRL-2024-BANK-01"
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-300 mb-2">Dominio Bancario</label>
            <div className="relative">
              <select
                value={config.domain}
                onChange={(e) => setConfig({ ...config, domain: e.target.value })}
                className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white appearance-none"
              >
                <option value="">Selecciona el área de negocio</option>
                {domains.map((d) => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
            </div>
            <p className="mt-1 text-xs text-slate-500">Esto ayuda a la IA a seleccionar el contexto terminológico adecuado.</p>
          </div>
        </div>
      </section>

      {/* Section 2: Documentation Sources */}
      <section className="bg-surface-dark border border-slate-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-xs font-bold text-white">2</span>
            <h3 className="text-lg font-semibold text-white">Fuentes de Documentación</h3>
          </div>
          <span className="text-xs text-slate-500">Integraciones activas: {(config.jira_enabled ? 1 : 0) + (config.confluence_enabled ? 1 : 0)}</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Jira */}
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold">J</span>
                </div>
                <div>
                  <p className="font-medium text-white">Jira Software</p>
                  <p className="text-xs text-slate-400">Importar Historias de Usuario</p>
                </div>
              </div>
              <Toggle
                enabled={config.jira_enabled}
                onChange={() => setConfig({ ...config, jira_enabled: !config.jira_enabled })}
              />
            </div>
            {config.jira_enabled && (
              <div className="space-y-3">
                <input
                  type="url"
                  value={config.jira_url}
                  onChange={(e) => setConfig({ ...config, jira_url: e.target.value })}
                  placeholder="https://tudominio.atlassian.net"
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm"
                />
                <input
                  type="text"
                  value={config.jira_user}
                  onChange={(e) => setConfig({ ...config, jira_user: e.target.value })}
                  placeholder="Usuario / Email Jira"
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm"
                />
                <button
                  onClick={testJiraConnection}
                  disabled={testingJira}
                  className="text-xs text-primary hover:underline"
                >
                  {testingJira ? 'Testing...' : 'Verificar Acceso'}
                </button>
              </div>
            )}
          </div>

          {/* Confluence */}
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold">C</span>
                </div>
                <div>
                  <p className="font-medium text-white">Confluence</p>
                  <p className="text-xs text-slate-400">Leer especificaciones funcionales</p>
                </div>
              </div>
              <Toggle
                enabled={config.confluence_enabled}
                onChange={() => setConfig({ ...config, confluence_enabled: !config.confluence_enabled })}
              />
            </div>
            {config.confluence_enabled && (
              <div className="space-y-3">
                <input
                  type="url"
                  value={config.confluence_url}
                  onChange={(e) => setConfig({ ...config, confluence_url: e.target.value })}
                  placeholder="https://tudominio.atlassian.net/wiki"
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm"
                />
                <input
                  type="text"
                  value={config.confluence_user}
                  onChange={(e) => setConfig({ ...config, confluence_user: e.target.value })}
                  placeholder="Usuario / Email Confluence"
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm"
                />
                <input
                  type="password"
                  value={config.confluence_api_key}
                  onChange={(e) => setConfig({ ...config, confluence_api_key: e.target.value })}
                  placeholder="Confluence API Key"
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm"
                />
                <button
                  onClick={testConfluenceConnection}
                  disabled={testingConfluence}
                  className="text-xs text-primary hover:underline"
                >
                  {testingConfluence ? 'Testing...' : 'Verificar Acceso'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* File Upload */}
        <RoleGuard allowed={['admin', 'qa_lead']}>
          <div className="mt-6 bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xl">🔑</span>
              <h3 className="font-medium text-white">Credenciales de Acceso Jira</h3>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Jira API Token <span className="text-red-400">*</span></label>
              <input
                type="password"
                value={config.jira_api_key || ''}
                onChange={(e) => setConfig({ ...config, jira_api_key: e.target.value })}
                placeholder="Pegar el token aquí (ATATT3xFf...)"
                className="w-full px-4 py-2.5 bg-slate-900 border border-slate-600 rounded-lg text-white font-mono text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <p className="text-xs text-slate-500 mt-2">
                Necesario para evitar errores 401. Genera tu token en: <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Atlassian Security</a>.
              </p>
            </div>
          </div>
        </RoleGuard>
      </section>

      {/* Section 3: API Keys (MCP Integration) */}
      <section className="bg-surface-dark border border-slate-800 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-6 h-6 bg-purple-500/20 rounded-full flex items-center justify-center"><KeyRound className="w-3.5 h-3.5 text-purple-400" /></span>
          <h3 className="text-lg font-semibold text-white">API Keys — MCP Integration</h3>
        </div>
        <p className="text-xs text-slate-500 mb-5">Genera claves para que el paquete SmartCase MCP autentique y descargue suites de prueba desde el IDE.</p>

        {/* Create new key */}
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={newKeyLabel}
            onChange={e => setNewKeyLabel(e.target.value)}
            placeholder="Ej: MCP – CI/CD pipeline"
            className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:outline-none"
            onKeyDown={e => e.key === 'Enter' && handleCreateKey()}
          />
          <button
            onClick={handleCreateKey}
            disabled={creatingKey || !newKeyLabel.trim()}
            className="flex items-center gap-1.5 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {creatingKey ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Nueva
          </button>
        </div>

        {/* Key list */}
        {loadingKeys ? (
          <div className="text-center py-6"><Loader2 className="w-6 h-6 animate-spin text-slate-500 mx-auto" /></div>
        ) : apiKeys.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-6">No hay API Keys activas. Crea una para conectar tu IDE.</p>
        ) : (
          <div className="space-y-2">
            {apiKeys.map(k => (
              <div key={k.id} className={`flex items-center justify-between px-3 py-2.5 rounded-lg border ${k.is_active ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-900/50 border-slate-800 opacity-50'
                }`}>
                <div className="flex items-center gap-3 min-w-0">
                  <KeyRound className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white truncate">{k.label}</p>
                    <p className="text-xs text-slate-500 font-mono">{k.key_prefix}••••••••••••••••••••</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="text-right hidden sm:block">
                    <p className="text-[10px] text-slate-500">Creada {new Date(k.created_at).toLocaleDateString()}</p>
                    {k.last_used_at && <p className="text-[10px] text-slate-500">Último uso {new Date(k.last_used_at).toLocaleDateString()}</p>}
                  </div>
                  {k.is_active && (
                    <button
                      onClick={() => handleRevokeKey(k.id)}
                      disabled={revokingKeyId === k.id}
                      className="p-1.5 rounded hover:bg-red-500/10 text-slate-500 hover:text-red-400 transition-colors"
                      title="Revocar"
                    >
                      {revokingKeyId === k.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Raw Key Modal (shown ONCE after creation) */}
      {rawKeyModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-slate-900 border border-purple-500/30 rounded-xl p-6 max-w-lg w-full shadow-2xl">
            <div className="flex items-center gap-2 mb-3">
              <Eye className="w-5 h-5 text-purple-400" />
              <h3 className="text-white font-bold">Tu nueva API Key</h3>
            </div>
            <p className="text-sm text-yellow-400 mb-3">⚠️ Cópiala ahora. No la mostraremos de nuevo.</p>
            <div className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 mb-4">
              <code className="flex-1 text-sm text-green-400 font-mono break-all">{rawKeyModal}</code>
              <button
                onClick={() => { navigator.clipboard.writeText(rawKeyModal); }}
                className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-white transition-colors flex-shrink-0"
                title="Copiar"
              ><Copy className="w-4 h-4" /></button>
            </div>
            <button
              onClick={() => setRawKeyModal(null)}
              className="w-full py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium text-sm transition-colors"
            >Listo, ya la guardé</button>
          </div>
        </div>
      )}
      {/* Section 4: Observability */}
      <section className="bg-slate-900 border border-slate-700 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-6 h-6 bg-purple-500/20 text-purple-400 rounded-full flex items-center justify-center text-xs font-bold">4</span>
          <h3 className="text-lg font-semibold text-white">Observabilidad</h3>
        </div>

        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-600/20 rounded-lg flex items-center justify-center">
                <span className="text-purple-400 font-bold text-sm">LF</span>
              </div>
              <div>
                <p className="font-medium text-white">Langfuse Tracing</p>
                <p className="text-xs text-slate-400">Registra trazas de las llamadas LLM en Langfuse</p>
              </div>
            </div>
            <Toggle
              enabled={!!config.langfuse_enabled}
              onChange={() => setConfig({ ...config, langfuse_enabled: !config.langfuse_enabled })}
            />
          </div>
          {!config.langfuse_enabled && (
            <p className="text-xs text-slate-500 mt-3">
              Las trazas LLM están desactivadas para este proyecto. Actívalas si quieres observabilidad en Langfuse.
            </p>
          )}
          {config.langfuse_enabled && (
            <p className="text-xs text-slate-400 mt-3">
              Las llamadas a Phase 1 (POM), Phase 2 (Spec) y Phase 3 (Review) quedarán registradas en Langfuse con modelo, tokens y metadata.
            </p>
          )}
        </div>
      </section>

      {/* Section 5: Danger Zone (Admin Only) */}
      {config.id && (
        <RoleGuard allowed={['admin']}>
          <section className="bg-red-500/5 border border-red-500/20 rounded-xl p-6 mt-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-6 h-6 bg-red-500/20 text-red-500 rounded-full flex items-center justify-center text-xs font-bold">!</span>
              <h3 className="text-lg font-semibold text-red-500">Zona de Peligro</h3>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">Eliminar Proyecto</p>
                <p className="text-sm text-slate-400">Esta acción no se puede deshacer. El proyecto quedará marcado como eliminado.</p>
              </div>
              <button
                onClick={handleDelete}
                disabled={isSaving}
                className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/50 rounded-lg transition-colors font-medium text-sm disabled:opacity-50"
              >
                {isSaving ? 'Eliminando...' : 'Eliminar Proyecto'}
              </button>
            </div>
          </section>
        </RoleGuard>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-4">
        <button className="px-6 py-2.5 text-slate-400 hover:text-white transition-colors">
          Cancelar
        </button>
        <RoleGuard allowed={['admin', 'qa_lead']}>
          <button
            onClick={handleSave}
            disabled={isSaving || isProjectLoading}
            className="flex items-center gap-2 px-6 py-2.5 bg-primary hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Guardando...' : (config.id ? 'Guardar Cambios' : 'Crear Proyecto y Generar')}
          </button>
        </RoleGuard>
      </div>
    </div>
  );
};

export default Configuration;
